// Who is in this workspace, what they may do, and — since ADR-0013 — whether
// they are inside your organisation or working for it from outside.
//
// Roles are bundles of permissions, not job titles: a video editor and a
// copywriter are both Creators. What separates people here is authority —
// create, review, approve, schedule, publish — and the screen says so, because
// the difference between "Creator" and "Publisher" is the difference between
// writing a post and putting it in front of someone's audience.
//
// The layout answers the owner's question in the order they ask it: who is
// outside my company and what can they reach (top right), how do I let someone
// in (top left), and then the full list of everyone with the controls on it.
// Outside companies are a *summary* — one row each, clicking one filters the
// list below — rather than a second place with its own copy of the controls.
// One person, one row with actions on it, is the thing that keeps this screen
// honest about who can do what.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWorkspace } from "../lib/WorkspaceContext";
import { listConnections } from "../lib/api";
import { kindOf, WORKSPACE_KIND } from "../lib/workspace";
import {
  changeRoleAssignment, grantRole, grantRoleForBrand, inviteMember, invitationLink,
  listInvitations, listPeople, listRoleMatrix, listRoles, removeMember, resendInvitation, revokeInvitation, revokeRole, setMemberRelationship,
  type Invitation, type InviteResult, type Permission, type Person, type Relationship,
  type RemovalOutcome, type Role, type RoleMatrix,
} from "../lib/people";
import { listBrands, type Brand } from "../lib/brands";
import { AuthorisedAgencies } from "../components/AuthorisedAgencies";
// Shared with the toolbar's account chip, so a person's colour is the same in
// both places.
import { Monogram } from "../components/Monogram";
// Every call on this screen goes to the server. None of them are instant, and
// several send email.
import { Loading, Spinner } from "../components/Spinner";
import { logError } from "../lib/logger";
import { PLATFORM_LABEL, type Connection, type Platform } from "../lib/types";

/** YYYY-MM-DD, which is what <input type="date"> speaks. */
function dateInputValue(daysFromNow: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}

/** End of the chosen day, so "ends on the 30th" includes the 30th. */
function endOfDayIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(`${value}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** Outside people default to a fixed term. An engagement that never ends is the
 *  thing that quietly outlives the engagement. */
const DEFAULT_EXTERNAL_DAYS = 90;

const NO_COMPANY = "Company not named";

/**
 * A member and a pending invitation are the same thing to the person reading
 * this screen — someone with access, or about to have it. Keeping them in two
 * separate tables meant the answer to "who can reach my Facebook Page" was
 * spread over two cards.
 */
type Entry =
  | { type: "member"; id: string; email: string; person: Person }
  | { type: "invite"; id: string; email: string; invitation: Invitation };

const entryRelationship = (e: Entry): Relationship =>
  e.type === "member" ? e.person.relationship : e.invitation.relationship;

const entryOrganisation = (e: Entry): string =>
  (e.type === "member" ? e.person.organisation : e.invitation.organisation)?.trim() || NO_COMPANY;

/** The accounts an entry can actually reach, resolved through the connection list. */
function reachOf(e: Entry, connections: Connection[]): {
  whole: boolean; platforms: Platform[]; names: string[];
} {
  const live = connections.filter((c) => c.status !== "revoked");
  const scopes = e.type === "member"
    ? e.person.assignments.map((a) => ({ type: a.scope_type, id: a.scope_id }))
    : [{ type: e.invitation.scope_type, id: e.invitation.scope_id }];

  if (scopes.length === 0) return { whole: false, platforms: [], names: [] };
  if (scopes.some((s) => s.type === "workspace")) {
    return { whole: true, platforms: [...new Set(live.map((c) => c.platform))], names: [] };
  }
  const matched = live.filter((c) => scopes.some((s) => s.id === c.id));
  return {
    whole: false,
    platforms: [...new Set(matched.map((c) => c.platform))],
    names: matched.map((c) => c.display_name),
  };
}

/** Every expiry attached to an entry, so "no end date" can be stated as a fact. */
function expiriesOf(e: Entry): { dates: string[]; openEnded: boolean } {
  if (e.type === "invite") {
    return {
      dates: e.invitation.access_expires_at ? [e.invitation.access_expires_at] : [],
      openEnded: !e.invitation.access_expires_at,
    };
  }
  const dates = e.person.assignments.map((a) => a.expires_at).filter((v): v is string => !!v);
  return { dates, openEnded: e.person.assignments.some((a) => !a.expires_at) };
}

export function People() {
  const { active, can, refresh } = useWorkspace();
  const workspaceId = active?.workspace_id ?? null;
  const kind = kindOf(active);

  const [people, setPeople] = useState<Person[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Which action is in flight, not merely whether one is. A single boolean
  // disables the whole screen but cannot say *where* the work is happening, so
  // the spinner ends up on nothing or on everything. The key is the identity of
  // the control that started it.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;
  const [loading, setLoading] = useState(true);
  // What a removal left standing. On an agency workspace, Remove reaches into
  // every client this workspace engaged the person out to — and cannot reach a
  // grant the client made directly (ADR-0021 L-B, L-C).
  const [removal, setRemoval] = useState<RemovalOutcome | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("creator");
  const [scopeId, setScopeId] = useState<string>("");
  const [relationship, setRelationship] = useState<Relationship>("internal");
  const [organisation, setOrganisation] = useState("");
  const [accessEnds, setAccessEnds] = useState("");
  // How the invitation reaches them. Email is the default when a provider is
  // configured; the link is always available, because it is what actually
  // works when email is not.
  const [sendEmail, setSendEmail] = useState(true);
  const [lastInvite, setLastInvite] = useState<InviteResult | null>(null);
  const [roleHelpOpen, setRoleHelpOpen] = useState(false);

  // "" = everyone. "internal" / "external", or "org:BluePeak Agency".
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setPeople(await listPeople(workspaceId));
    setInvites(await listInvitations(workspaceId).catch(() => []));
    setRoles(await listRoles());
    setConnections(await listConnections(workspaceId).catch(() => []));
    setBrands(await listBrands(workspaceId).catch(() => []));
  }, [workspaceId]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((e) => {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "load_people", workspaceId });
      })
      .finally(() => setLoading(false));
  }, [load, workspaceId]);

  /**
   * Runs one action and reloads what it changed.
   *
   * `key` stays set for the whole round trip — the call *and* the reload that
   * follows it — because the list is stale until both are done, and a spinner
   * that stops while the old numbers are still on screen is worse than none.
   */
  const run = useCallback(async (fn: () => Promise<unknown>, key = "page") => {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await load();
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusyKey(null);
    }
  }, [load, refresh]);

  const mayInvite = can("member.invite");
  const mayManage = can("member.manage");

  const entries = useMemo<Entry[]>(() => [
    ...people.map((p): Entry => ({
      type: "member", id: `m:${p.user_id}`, email: p.email ?? p.user_id, person: p,
    })),
    ...invites.map((i): Entry => ({
      type: "invite", id: `i:${i.id}`, email: i.email, invitation: i,
    })),
  ].sort((a, b) => a.email.localeCompare(b.email)), [people, invites]);

  const outside = useMemo(
    () => entries.filter((e) => entryRelationship(e) === "external"), [entries]);

  // One row per outside company: the summary an owner wants before they decide
  // anything. It is deliberately not a second set of per-person controls.
  const companies = useMemo(() => {
    const groups = new Map<string, Entry[]>();
    for (const e of outside) {
      const key = entryOrganisation(e);
      groups.set(key, [...(groups.get(key) ?? []), e]);
    }
    return [...groups.entries()]
      .map(([name, members]) => {
        const reach = members.map((m) => reachOf(m, connections));
        const expiry = members.map(expiriesOf);
        return {
          name,
          members,
          whole: reach.some((r) => r.whole),
          platforms: [...new Set(reach.flatMap((r) => r.platforms))],
          accountNames: [...new Set(reach.flatMap((r) => r.names))],
          allPending: members.every((m) => m.type === "invite"),
          openEnded: expiry.some((x) => x.openEnded),
          soonest: expiry.flatMap((x) => x.dates).sort()[0],
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [outside, connections]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (filter === "internal" && entryRelationship(e) !== "internal") return false;
      if (filter === "external" && entryRelationship(e) !== "external") return false;
      if (filter.startsWith("org:") && entryOrganisation(e) !== filter.slice(4)) return false;
      if (q && !e.email.toLowerCase().includes(q)
            && !entryOrganisation(e).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entries, filter, query]);

  const isExternal = relationship === "external";
  // The server refuses this too; hiding it here means the owner never composes
  // an invitation the database is going to reject.
  const invitableRoles = roles.filter((r) => !r.is_owner && !(isExternal && r.code === "admin"));

  function chooseRelationship(next: Relationship) {
    setRelationship(next);
    if (next === "external") {
      setAccessEnds((current) => current || dateInputValue(DEFAULT_EXTERNAL_DAYS));
      if (role === "admin") setRole("creator");
    } else {
      setOrganisation("");
    }
  }

  function focusCompany(name: string) {
    setFilter(`org:${name}`);
    setQuery("");
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const inviteBlocked = !email
    || (isExternal && !organisation.trim())
    || (isExternal && !accessEnds);

  if (!can("member.view")) {
    return (
      <div className="people-page">
        <div className="card muted">
          You do not have permission to see the people in this workspace.
        </div>
      </div>
    );
  }

  const counts = {
    all: entries.length,
    internal: entries.filter((e) => entryRelationship(e) === "internal").length,
    external: outside.length,
  };

  return (
    <div className="people-page">
      <header className="people-head">
        <div>
          <h1>People &amp; access</h1>
          <p className="muted">
            Work with your team and with agencies you trust — each with the access you choose,
            and nothing you did not.
          </p>
        </div>
        <span className="shield-pill">
          Access is limited to what you grant, and revocable at any time.
        </span>
      </header>

      {error && <div className="card err">{error}</div>}

      {removal && removal.retained.length > 0 && (
        <div className="card err">
          <strong>They still have access elsewhere.</strong>
          <p className="muted card-sub">
            Removing them here also ended the access this workspace had arranged for them at
            {" "}{removal.clients_affected === 1
              ? "1 client" : `${removal.clients_affected} clients`}. These were granted by the
            client directly, so they are not this workspace's to revoke:
          </p>
          <ul className="can-list">
            {removal.retained.map((r) => (
              <li className="cannot" key={r.client_workspace_id}>
                <strong>{r.client_name}</strong>
                {r.roles?.length > 0 && ` — ${r.roles.join(", ")}`}
              </li>
            ))}
          </ul>
          <button className="sm" onClick={() => setRemoval(null)}>Dismiss</button>
        </div>
      )}

      {removal && removal.retained.length === 0 && removal.clients_affected > 0 && (
        <div className="card notice ok-note">
          Their access also ended at {removal.clients_affected === 1
            ? "1 client" : `${removal.clients_affected} clients`} this workspace had placed them
          with. <button className="link" onClick={() => setRemoval(null)}>Dismiss</button>
        </div>
      )}

      <div className="people-grid">
        <div className="people-col">
          {mayInvite ? (
            <div className="card">
              <div className="card-head">
                <h2>Invite someone</h2>
                <button type="button" className="link field-help"
                        onClick={() => setRoleHelpOpen(true)}>
                  How roles work
                </button>
              </div>
              <p className="muted card-sub">
                Add a teammate or an agency contact, and set exactly what they may reach.
              </p>

              <fieldset className="relationship-choice">
                <legend>Who is this?</legend>
                <label className={!isExternal ? "method on" : "method"}>
                  <input type="radio" name="relationship" checked={!isExternal}
                         onChange={() => chooseRelationship("internal")} />
                  <span>
                    <strong>In my organisation</strong>
                    <span className="muted">An employee or someone on my own team.</span>
                  </span>
                </label>
                <label className={isExternal ? "method on" : "method"}>
                  <input type="radio" name="relationship" checked={isExternal}
                         onChange={() => chooseRelationship("external")} />
                  <span>
                    <strong>Outside my organisation</strong>
                    <span className="muted">An agency, a freelancer, a contractor.</span>
                  </span>
                </label>
              </fieldset>

              <div className="invite-form">
                <div>
                  <label htmlFor="invite-email">Email address</label>
                  <input id="invite-email" type="email" value={email}
                         placeholder="name@company.com"
                         onChange={(e) => setEmail(e.target.value)} />
                </div>

                <div>
                  <label htmlFor="invite-role">Role</label>
                  <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
                    {invitableRoles.map((r) => (
                      <option key={r.code} value={r.code}>{r.name}</option>
                    ))}
                  </select>
                </div>

                {isExternal && (
                  <div>
                    <label htmlFor="invite-org">Which company?</label>
                    <input id="invite-org" type="text" value={organisation}
                           placeholder="BluePeak Agency"
                           onChange={(e) => setOrganisation(e.target.value)} />
                  </div>
                )}

                <div>
                  <label htmlFor="invite-scope">Account scope</label>
                  <select id="invite-scope" value={scopeId}
                          onChange={(e) => setScopeId(e.target.value)}>
                    <option value="">Whole workspace</option>
                    {connections.filter((c) => c.status !== "revoked").map((c) => (
                      <option key={c.id} value={c.id}>{c.display_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="invite-until">
                    Access ends{isExternal ? "" : " (optional)"}
                  </label>
                  <input id="invite-until" type="date" value={accessEnds}
                         min={dateInputValue(1)}
                         onChange={(e) => setAccessEnds(e.target.value)} />
                </div>

                <div>
                  <label htmlFor="invite-method">Invite method</label>
                  <select id="invite-method" value={sendEmail ? "email" : "link"}
                          onChange={(e) => setSendEmail(e.target.value === "email")}>
                    <option value="email">Email invitation</option>
                    <option value="link">Give me the link</option>
                  </select>
                </div>
              </div>

              <p className="muted card-sub">
                {roles.find((r) => r.code === role)?.description}
                {scopeId
                  ? " This applies only to the selected account — not to anything else here."
                  : isExternal
                    ? " Not limited to one account: they reach everything their role allows."
                    : ""}
                {" "}
                {sendEmail
                  ? "We email the invitation and give you the link as well."
                  : "Nothing is emailed; send the link however you like."}
              </p>

              {isExternal && (
                <p className="notice card-sub">
                  Someone outside your organisation cannot be made <strong>Admin</strong> — that
                  role carries billing, membership, and the right to connect and disconnect social
                  accounts.
                </p>
              )}

              <button
                className={busyKey === "invite" ? "primary is-busy" : "primary"}
                disabled={busy || inviteBlocked}
                onClick={() => run(async () => {
                  const result = await inviteMember({
                    workspaceId: workspaceId!,
                    email,
                    role,
                    scopeType: scopeId ? "connection" : "workspace",
                    scopeId: scopeId || null,
                    sendEmail,
                    relationship,
                    organisation: isExternal ? organisation.trim() : null,
                    accessExpiresAt: endOfDayIso(accessEnds),
                  });
                  setEmail("");
                  setLastInvite(result);
                }, "invite")}
              >
                {busyKey === "invite" && <Spinner />}
                {busyKey === "invite"
                  /* Emailing is the slow half and the half that can fail, so
                     the label says which one is running. */
                  ? (sendEmail ? "Sending invitation…" : "Creating invitation…")
                  : (sendEmail ? "Send invitation" : "Create invitation")}
              </button>

              {lastInvite && (
                <div className={lastInvite.email.status === "sent" ? "notice ok-note" : "notice"}
                     style={{ marginTop: ".8rem" }}>
                  {/* What actually happened, not what was requested. An invitation
                      that silently never arrives is the failure mode worth spending
                      three sentences to avoid. */}
                  {lastInvite.email.status === "sent" && (
                    <>Invitation emailed to <strong>{lastInvite.invitation.email}</strong>. The link
                    is below as well, in case it does not arrive.</>
                  )}
                  {lastInvite.email.status === "skipped" && (
                    <><strong>Not emailed.</strong> {lastInvite.email.reason ?? "No email provider is configured."}
                    {" "}Send them this link instead:</>
                  )}
                  {lastInvite.email.status === "failed" && (
                    <><strong>The email could not be sent</strong>
                    {lastInvite.email.reason ? ` (${lastInvite.email.reason})` : ""}. The invitation
                    is valid — send them this link:</>
                  )}
                  {lastInvite.email.status === "not_requested" && <>Send them this link:</>}
                  <br />
                  <code className="invite-link">
                    {lastInvite.invite_url || invitationLink(lastInvite.invitation.token)}
                  </code>
                  <br />
                  <button
                    className="sm"
                    onClick={() => navigator.clipboard?.writeText(
                      lastInvite.invite_url || invitationLink(lastInvite.invitation.token))}
                  >
                    Copy link
                  </button>
                  {/* The link is no longer the only way in. Someone who loses
                      the email, or reads it on another device, now finds this
                      invitation waiting the next time they sign in with that
                      address — which is what stops an invitation quietly
                      expiring after 14 days. */}
                  <div className="muted" style={{ marginTop: ".5rem" }}>
                    They can also just sign in with{" "}
                    <strong>{lastInvite.invitation.email}</strong> — the invitation is waiting for
                    them there until{" "}
                    {new Date(lastInvite.invitation.expires_at).toLocaleDateString(undefined, {
                      day: "numeric", month: "long", year: "numeric",
                    })}.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card muted">
              You can see who is here, but not invite anyone. Ask an owner or admin.
            </div>
          )}
        </div>

        <div className="people-col">
          <div className="card">
            <div className="card-head">
              <h2>Outside your organisation</h2>
              {companies.length > 0 && (
                <span className="muted">
                  {counts.external === 1 ? "1 person" : `${counts.external} people`}
                  {" · "}
                  {companies.length === 1 ? "1 company" : `${companies.length} companies`}
                </span>
              )}
            </div>

            {loading ? (
              <Loading label="Loading outside companies…" />
            ) : companies.length === 0 ? (
              <p className="muted card-sub" style={{ marginBottom: 0 }}>
                Nobody outside your organisation has access. When you invite an agency or a
                freelancer, they are listed here — by company, with what they can reach and when
                it ends.
              </p>
            ) : (
              <div className="company-table" role="table" aria-label="Outside companies">
                <div className="company-row head" role="row">
                  <span role="columnheader">Company</span>
                  <span role="columnheader">Can reach</span>
                  <span role="columnheader">Access ends</span>
                  <span role="columnheader"><span className="visually-hidden">Actions</span></span>
                </div>
                {companies.map((c) => (
                  <div className="company-row" role="row" key={c.name}>
                    <span className="company-name" role="cell">
                      <Monogram label={c.name} />
                      <span>
                        <button type="button" className="link company-link"
                                onClick={() => focusCompany(c.name)}>
                          {c.name}
                        </button>
                        <span className="muted">
                          {c.members.length === 1 ? "1 person" : `${c.members.length} people`}
                          {c.allPending && " · not joined yet"}
                        </span>
                      </span>
                    </span>

                    <span role="cell">
                      {c.whole
                        ? <span className="chip warn">Whole workspace</span>
                        : c.platforms.length === 0
                          ? <span className="muted">Nothing yet</span>
                          : <PlatformBadges platforms={c.platforms} names={c.accountNames} />}
                    </span>

                    <span role="cell">
                      {c.openEnded
                        ? <span className="chip warn">No end date</span>
                        : c.soonest
                          ? <span className="chip">{shortDate(c.soonest)}</span>
                          : <span className="muted">—</span>}
                    </span>

                    <span className="company-actions" role="cell">
                      {mayManage && (
                        <button
                          type="button"
                          className={busyKey === `company:${c.name}` ? "sm danger is-busy" : "sm danger"}
                          disabled={busy}
                          onClick={() => endAccessForCompany(c.name, c.members, run, workspaceId!)}
                        >
                          {busyKey === `company:${c.name}` && <Spinner />}
                          {busyKey === `company:${c.name}` ? "Ending access…" : "End access"}
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* An agency you authorised is a different thing from a person you
              invited: one authorisation, its own people, and one revoke that is
              a single transaction rather than a loop (ADR-0015). */}
          <AuthorisedAgencies
            workspaceId={workspaceId!}
            connections={connections}
            roles={roles}
            mayManage={mayManage}
            onChanged={() => void load()}
          />

          {(mayInvite || counts.external > 0) && (
            <OutsideAccessNotice kindLabel={WORKSPACE_KIND[kind].label} />
          )}
        </div>
      </div>

      <div className="card" ref={listRef}>
        <div className="card-head">
          <h2>Everyone with access</h2>
          <input
            className="people-search"
            type="search"
            value={query}
            placeholder="Search by email or company…"
            aria-label="Search people"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="segmented people-filter" role="group" aria-label="Filter people">
          <FilterTab now={filter} set={setFilter} value="" label="Everyone" count={counts.all} />
          <FilterTab now={filter} set={setFilter} value="internal" label="Your team"
                     count={counts.internal} />
          <FilterTab now={filter} set={setFilter} value="external" label="Outside"
                     count={counts.external} />
          {filter.startsWith("org:") && (
            <FilterTab now={filter} set={setFilter} value={filter}
                       label={filter.slice(4)} count={visible.length} />
          )}
        </div>

        {loading && <Loading label="Loading everyone with access…" />}

        {!loading && visible.length === 0 && (
          <p className="muted">Nobody here matches that.</p>
        )}

        {visible.map((entry) => entry.type === "member" ? (
          <MemberRow
            key={entry.id}
            person={entry.person}
            roles={roles}
            connections={connections}
            brands={brands}
            busy={busy}
            busyKey={busyKey}
            mayManage={mayManage}
            onRun={run}
            onRemoved={setRemoval}
            workspaceId={workspaceId!}
          />
        ) : (
          <InviteRow
            key={entry.id}
            invitation={entry.invitation}
            roles={roles}
            busy={busy}
            busyKey={busyKey}
            mayInvite={mayInvite}
            mayManage={mayManage}
            onRun={run}
          />
        ))}
      </div>

      {roleHelpOpen && <RoleHelpDrawer roles={roles} onClose={() => setRoleHelpOpen(false)} />}
    </div>
  );
}

/**
 * Ends access for everyone at one company.
 *
 * Sequential, and it reports exactly who is *still* in the workspace if part of
 * it fails. This is not a transaction — a tidy success message over a partial
 * removal would leave someone with access nobody knows about.
 */
function endAccessForCompany(
  name: string,
  members: Entry[],
  run: (fn: () => Promise<unknown>, key?: string) => Promise<void>,
  workspaceId: string,
) {
  const names = members.map((m) => m.email).join("\n");
  if (!confirm(
    `End access for everyone at ${name}?\n\n${names}\n\n`
    + "They are removed one at a time, so if one fails the others are still removed. "
    + "Scheduled posts stay; access ends immediately.")) return;

  void run(async () => {
    const failed: string[] = [];
    for (const m of members) {
      try {
        if (m.type === "member") await removeMember(workspaceId, m.person.user_id);
        else await revokeInvitation(m.invitation.id);
      } catch (e: any) {
        failed.push(`${m.email}: ${e.message ?? String(e)}`);
      }
    }
    if (failed.length) throw new Error(`Still has access — ${failed.join("; ")}`);
  }, `company:${name}`);
}

function FilterTab({ now, set, value, label, count }: {
  now: string; set: (v: string) => void; value: string; label: string; count: number;
}) {
  return (
    <button
      type="button"
      className={now === value ? "on" : ""}
      aria-pressed={now === value}
      onClick={() => set(value)}
    >
      {label} <span className="tab-count">{count}</span>
    </button>
  );
}

/** Initials, coloured from the name. There are no avatars to load — `auth.users`
 *  gives an email and nothing else — and a monogram beats a grey circle. */
function PlatformBadges({ platforms, names }: { platforms: Platform[]; names: string[] }) {
  const title = names.length ? names.join(", ") : platforms.map((p) => PLATFORM_LABEL[p]).join(", ");
  return (
    <span className="platform-badges" title={title}>
      {platforms.map((p) => (
        <span key={p} className={`platform-badge ${p}`} aria-label={PLATFORM_LABEL[p]}>
          {PLATFORM_LABEL[p][0]}
        </span>
      ))}
      {names.length > platforms.length && (
        <span className="muted">+{names.length - platforms.length}</span>
      )}
    </span>
  );
}

/**
 * The promise ADR-0011 §6 makes, in the words of the person relying on it.
 *
 * It was previously true but stated only inside a drawer behind "Learn more",
 * which is the wrong place for the reassurance that decides whether someone is
 * willing to let an agency near their Page at all.
 */
function OutsideAccessNotice({ kindLabel }: { kindLabel: string }) {
  return (
    <div className="card outside-explainer">
      <h2>What someone outside can do</h2>
      <ul className="can-list">
        <li className="can">Draft, edit and submit content for review.</li>
        <li className="can">Schedule and publish — if their role allows — to accounts you allow.</li>
        <li className="can">See the results of what they published.</li>
        <li className="cannot">Never sees your passwords or access tokens, at any role.</li>
        <li className="cannot">Cannot connect, reconnect or disconnect a social account.</li>
        <li className="cannot">Cannot change billing, ownership, or who else is here.</li>
        <li className="cannot">Cannot reach an account you did not scope them to.</li>
      </ul>
      <p className="muted" style={{ fontSize: ".8rem", marginBottom: 0 }}>
        This workspace is set up as a <strong>{kindLabel.toLowerCase()}</strong>.
      </p>
    </div>
  );
}

/**
 * Where a role applies, as one <select> value: "" is the whole workspace,
 * "c:<id>" one account, "b:<id>" every account filed under a brand today.
 * A brand is never stored as a scope (ADR-0029) — it is expanded on the server
 * into one grant per account, so it is offered when adding and not when editing.
 */
function ScopeSelect({ value, onChange, connections, brands, disabled, label }: {
  value: string;
  onChange: (value: string) => void;
  connections: Connection[];
  brands?: Brand[];
  disabled: boolean;
  label: string;
}) {
  const live = connections.filter((c) => c.status !== "revoked");
  const filed = (brands ?? [])
    .map((b) => ({ brand: b, count: live.filter((c) => c.brand_id === b.id).length }))
    .filter((f) => f.count > 0);
  return (
    <select value={value} disabled={disabled} aria-label={label}
            onChange={(e) => onChange(e.target.value)}>
      <option value="">Whole workspace</option>
      {filed.length > 0 && (
        <optgroup label="Brand">
          {filed.map(({ brand, count }) => (
            <option key={brand.id} value={`b:${brand.id}`}>
              {brand.name} ({count === 1 ? "1 account" : `${count} accounts`})
            </option>
          ))}
        </optgroup>
      )}
      {live.length > 0 && (
        <optgroup label="One account">
          {live.map((c) => (
            <option key={c.id} value={`c:${c.id}`}>Only {c.display_name}</option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

function MemberRow({
  person: p, roles, connections, brands, busy, busyKey, mayManage, onRun, onRemoved, workspaceId,
}: {
  person: Person;
  roles: Role[];
  connections: Connection[];
  brands: Brand[];
  busy: boolean;
  busyKey: string | null;
  mayManage: boolean;
  onRun: (fn: () => Promise<unknown>, key?: string) => Promise<void>;
  onRemoved: (outcome: RemovalOutcome) => void;
  workspaceId: string;
}) {
  const isExternal = p.relationship === "external";
  // Namespaced by person: two rows can hold the same action, and only the row
  // that was clicked should show it running.
  const keyFor = (action: string) => `member:${p.user_id}:${action}`;
  const running = (action: string) => busyKey === keyFor(action);
  // Adding a role to an outsider inherits the same rule as inviting one: it
  // ends on a date. Prefilled rather than prompted, and editable in place, so
  // the open-ended grant is the one that takes a deliberate action.
  const [until, setUntil] = useState(
    isExternal ? dateInputValue(DEFAULT_EXTERNAL_DAYS) : "");
  // Which accounts the next role covers (see ScopeSelect). Without it every
  // role added here was workspace-wide, whatever the admin meant — the invite
  // form had a scope and this row did not.
  const [scope, setScope] = useState("");
  const [expanded, setExpanded] = useState(false);
  // The one role being edited in place, if any.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ role: "", scope: "", until: "" });
  const mayEdit = mayManage && !p.is_owner;
  const grantable = roles.filter((r) => !r.is_owner && !(isExternal && r.code === "admin"));
  const pickedBrand = scope.startsWith("b:")
    ? brands.find((b) => b.id === scope.slice(2)) ?? null : null;

  const startEdit = (a: Person["assignments"][number]) => {
    setEditing(a.id);
    setDraft({
      role: a.role,
      scope: a.scope_type === "connection" && a.scope_id ? `c:${a.scope_id}` : "",
      until: a.expires_at ? a.expires_at.slice(0, 10) : "",
    });
  };

  const addRole = (role: string) => {
    const expiresAt = endOfDayIso(until);
    void onRun(() => pickedBrand
      ? grantRoleForBrand({
          workspaceId, userId: p.user_id, role, brandId: pickedBrand.id, expiresAt,
        })
      : grantRole({
          workspaceId, userId: p.user_id, role,
          scopeType: scope ? "connection" : "workspace",
          scopeId: scope ? scope.slice(2) : null,
          expiresAt,
        }), keyFor("grant"));
  };

  const reach = reachOf({ type: "member", id: "", email: "", person: p }, connections);

  return (
    <div className={expanded ? "person open" : "person"}>
      <div className="person-main">
        <Monogram label={p.email ?? p.user_id} />

        <div className="person-body">
          <div className="person-head">
            <strong>{p.email ?? p.user_id}</strong>
            {p.is_owner && <span className="chip ok">Owner</span>}
            {isExternal && (
              <span className="chip warn">
                Outside{p.organisation ? ` · ${p.organisation}` : ""}
              </span>
            )}
          </div>

          <div className="person-roles">
            {p.assignments.length === 0 && (
              <span className="muted">No role — they can see nothing in this workspace.</span>
            )}
            {p.assignments.map((a) => editing === a.id ? (
              <span key={a.id} className="role-edit">
                <select value={draft.role} disabled={busy} aria-label="Role"
                        onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
                  {/* Keep the current role listed even when it is one that could
                      not be granted fresh, so opening the editor changes nothing. */}
                  {!grantable.some((r) => r.code === draft.role) && (
                    <option value={draft.role}>{a.role_name}</option>
                  )}
                  {grantable.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                </select>
                <ScopeSelect value={draft.scope} disabled={busy} connections={connections}
                             label="Which accounts this role covers"
                             onChange={(v) => setDraft({ ...draft, scope: v })} />
                <label className="inline-until">
                  <span className="muted">ends</span>
                  <input type="date" value={draft.until} min={dateInputValue(1)} disabled={busy}
                         aria-label="End date"
                         onChange={(e) => setDraft({ ...draft, until: e.target.value })} />
                </label>
                <button className={running(`edit:${a.id}`) ? "sm primary is-busy" : "sm primary"}
                        disabled={busy}
                        onClick={() => void onRun(async () => {
                          await changeRoleAssignment({
                            assignmentId: a.id,
                            role: draft.role,
                            scopeType: draft.scope ? "connection" : "workspace",
                            scopeId: draft.scope ? draft.scope.slice(2) : null,
                            expiresAt: endOfDayIso(draft.until),
                          });
                          setEditing(null);
                        }, keyFor(`edit:${a.id}`))}>
                  {running(`edit:${a.id}`) && <Spinner />}
                  {running(`edit:${a.id}`) ? "Saving…" : "Save"}
                </button>
                <button className="sm" disabled={busy} onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </span>
            ) : (
              <span key={a.id} className="chip role-chip">
                {a.role_name}
                {a.scope_type === "connection" && <> · only {a.scope_name ?? "one account"}</>}
                {a.expires_at
                  ? <> · until {shortDate(a.expires_at)}</>
                  : isExternal && <> · no end date</>}
                {mayEdit && (
                  <button className="link chip-x" title="Change this role" disabled={busy}
                          aria-label={`Change the ${a.role_name} role`}
                          onClick={() => startEdit(a)}>✎</button>
                )}
                {mayEdit && (
                  /* No room for a word here, so the ring replaces the × and
                     carries the label for screen readers instead. */
                  <button className={running(`revoke:${a.id}`) ? "link chip-x is-busy" : "link chip-x"}
                          title="Remove this role" disabled={busy}
                          onClick={() => onRun(() => revokeRole(a.id), keyFor(`revoke:${a.id}`))}>
                    {running(`revoke:${a.id}`)
                      ? <Spinner label={`Removing the ${a.role_name} role`} />
                      : "×"}
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>

        <div className="person-reach">
          {reach.whole
            ? <span className="muted">Whole workspace</span>
            : reach.platforms.length > 0
              ? <PlatformBadges platforms={reach.platforms} names={reach.names} />
              : <span className="muted">—</span>}
        </div>

        {mayManage && !p.is_owner && (
          <button className="row-toggle" aria-expanded={expanded}
                  aria-label={`Manage ${p.email ?? "this person"}`}
                  onClick={() => setExpanded((v) => !v)}>⋮</button>
        )}
      </div>

      {expanded && mayManage && !p.is_owner && (
        <div className="row person-actions">
          {/* A <select> cannot hold a spinner, so it sits beside it — the
              grant is a server call like any other and the row must say so.
              The scope comes first: choosing a role saves at once. */}
          <ScopeSelect value={scope} onChange={setScope} disabled={busy}
                       connections={connections} brands={brands}
                       label={`Which accounts the new role covers for ${p.email ?? "this person"}`} />
          <select
            defaultValue=""
            disabled={busy}
            aria-label="Add a role"
            onChange={(e) => {
              const value = e.target.value;
              e.target.value = "";
              if (value) addRole(value);
            }}
          >
            <option value="">Add a role…</option>
            {grantable.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
          </select>
          {running("grant") && (
            <span className="muted loading-row" role="status">
              <Spinner /> Adding the role…
            </span>
          )}
          {pickedBrand && (
            /* ADR-0029: a brand is filing, not access. Say what the grant is. */
            <span className="muted brand-scope-note">
              Adds the role on each account filed under {pickedBrand.name} today.
              Accounts filed there later are not included.
            </span>
          )}

          {isExternal && (
            <label className="inline-until">
              <span className="muted">ends</span>
              <input type="date" value={until} min={dateInputValue(1)} disabled={busy}
                     aria-label={`End date for roles added to ${p.email ?? "this person"}`}
                     onChange={(e) => setUntil(e.target.value)} />
            </label>
          )}

          {/* Every member who existed before ADR-0013 reads as internal, which
              is wrong for the agency people already here. This is the correction. */}
          <button
            className={running("relationship") ? "sm is-busy" : "sm"}
            disabled={busy}
            onClick={() => {
              if (isExternal) {
                void onRun(() => setMemberRelationship({
                  workspaceId, userId: p.user_id, relationship: "internal",
                }), keyFor("relationship"));
                return;
              }
              const org = prompt(
                `Which company does ${p.email ?? "this person"} work for?`,
                p.organisation ?? "");
              if (org === null) return;
              void onRun(() => setMemberRelationship({
                workspaceId, userId: p.user_id, relationship: "external",
                organisation: org.trim() || null,
              }), keyFor("relationship"));
            }}
          >
            {running("relationship") && <Spinner />}
            {running("relationship")
              ? "Saving…"
              : isExternal ? "Mark as in my organisation" : "Mark as outside"}
          </button>

          <button
            className={running("remove") ? "sm danger is-busy" : "sm danger"}
            disabled={busy}
            onClick={() => {
              /* On an agency workspace this is not a local action: it also ends
                 every grant this workspace arranged for them at a client. The
                 confirm has to say so before it happens, not after. */
              if (confirm(
                `Remove ${p.email ?? "this person"} from the workspace?\n\n`
                + "Their scheduled posts stay, their access ends immediately. If this "
                + "workspace has placed them with any clients, that access ends too.")) {
                void onRun(
                  () => removeMember(workspaceId, p.user_id).then(onRemoved),
                  keyFor("remove"));
              }
            }}
          >
            {running("remove") && <Spinner />}
            {running("remove") ? "Removing…" : "Remove"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Someone invited but not yet joined. Same list, because to the owner it is the
 *  same question: who has access to this workspace? */
function InviteRow({ invitation: i, roles, busy, busyKey, mayInvite, mayManage, onRun }: {
  invitation: Invitation;
  roles: Role[];
  busy: boolean;
  busyKey: string | null;
  mayInvite: boolean;
  mayManage: boolean;
  onRun: (fn: () => Promise<unknown>, key?: string) => Promise<void>;
}) {
  const revokeKey = `invite:${i.id}:revoke`;
  const revoking = busyKey === revokeKey;
  // Local to the row, not to the page: "sent" belongs next to the person it was
  // sent to, and a resend must not blank the whole list the way `onRun` does.
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  async function resend() {
    setSending(true);
    setOutcome(null);
    try {
      const result = await resendInvitation(i.id);
      // What happened, not what was asked for. "Sent" when no provider is
      // configured would be a lie the owner discovers a week later.
      setOutcome(
        result.email.status === "sent"
          ? `Sent again to ${i.email}.`
          : result.email.status === "skipped"
            ? `Not emailed — ${result.email.reason ?? "no email provider is configured"}. Copy the link instead.`
            : `The email could not be sent${result.email.reason ? ` (${result.email.reason})` : ""}. The link still works.`,
      );
    } catch (e: any) {
      setOutcome(e.message ?? String(e));
      void logError("app", e, { step: "resend_invitation" });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="person pending">
      <div className="person-main">
        <Monogram label={i.email} />

        <div className="person-body">
          <div className="person-head">
            <strong>{i.email}</strong>
            <span className="chip">Invited</span>
            {i.relationship === "external" && (
              <span className="chip warn">
                Outside{i.organisation ? ` · ${i.organisation}` : ""}
              </span>
            )}
          </div>
          <div className="person-roles">
            <span className="chip role-chip">
              {roles.find((r) => r.code === i.role_code)?.name ?? i.role_code}
              {i.access_expires_at
                ? <> · until {shortDate(i.access_expires_at)}</>
                : i.relationship === "external" && <> · no end date</>}
            </span>
            <span className="muted">link expires {shortDate(i.expires_at)}</span>
          </div>
        </div>

        <div className="person-reach">
          <span className="muted">Not joined yet</span>
        </div>

        <div className="row invite-actions">
          <button className="sm"
                  onClick={() => navigator.clipboard?.writeText(invitationLink(i.token))}>
            Copy link
          </button>
          {/* The commonest thing an owner wants from this row: "they never got
              it, send it again". Same invitation, same link — the expiry is
              pushed out server-side so the mail is not sent with a link that
              dies tomorrow. */}
          {mayInvite && (
            <button className={sending ? "sm is-busy" : "sm"}
                    disabled={busy || sending} onClick={() => void resend()}>
              {sending && <Spinner />}
              {sending ? "Sending…" : "Resend email"}
            </button>
          )}
          {mayManage && (
            <button className={revoking ? "sm danger is-busy" : "sm danger"}
                    disabled={busy || sending}
                    onClick={() => onRun(() => revokeInvitation(i.id), revokeKey)}>
              {revoking && <Spinner />}
              {revoking ? "Withdrawing…" : "Withdraw"}
            </button>
          )}
        </div>

        {outcome && <p className="muted invite-outcome">{outcome}</p>}
      </div>
    </div>
  );
}

/** Permission categories in the order the drawer reads them, with a heading a
 *  workspace owner would recognise. Anything the catalogue adds later that is
 *  not listed here still renders, under its own key. */
const CATEGORY_LABEL: Record<string, string> = {
  workspace: "Workspace",
  billing: "Billing",
  people: "People and teams",
  connections: "Connected accounts",
  projects: "Projects",
  media: "Media",
  content: "Content",
  delivery: "Delivery",
  insight: "Analytics and audit",
};

/**
 * The roles side by side, one row per permission.
 *
 * A list of eight descriptions answers "what is a Publisher"; it does not
 * answer "what does a Publisher have that a Creator does not", which is the
 * question someone actually has when they are choosing between them. The grid
 * is built from the catalogue tables, so a role added by a migration appears
 * here on its own — and a tick is the permission the server checks, not a
 * sentence about it.
 */
function RoleCompareTable({ roles: given, matrix }: { roles: Role[]; matrix: RoleMatrix }) {
  // Widest role on the left: Owner, then by how many permissions each holds.
  const roles = useMemo(() => [...given].sort((a, b) =>
    Number(b.is_owner) - Number(a.is_owner) ||
    (matrix.granted[b.code]?.size ?? 0) - (matrix.granted[a.code]?.size ?? 0)),
  [given, matrix.granted]);
  const groups = useMemo(() => {
    const byCategory = new Map<string, Permission[]>();
    for (const p of matrix.permissions) {
      byCategory.set(p.category, [...(byCategory.get(p.category) ?? []), p]);
    }
    return [...byCategory.entries()];
  }, [matrix.permissions]);

  const holds = (role: Role, key: string) =>
    role.is_owner || (matrix.granted[role.code]?.has(key) ?? false);

  return (
    <div className="table-scroll role-compare-scroll">
      <table className="plans role-compare">
        <thead>
          <tr>
            <th scope="col">Permission</th>
            {roles.map((r) => (
              <th scope="col" key={r.code}>
                {r.name}
                {r.is_owner && <span className="soon">everything</span>}
              </th>
            ))}
          </tr>
        </thead>
        {groups.map(([category, permissions]) => (
          <tbody key={category}>
            <tr className="role-compare-group">
              <th scope="colgroup" colSpan={roles.length + 1}>
                {CATEGORY_LABEL[category] ?? category}
              </th>
            </tr>
            {permissions.map((p) => (
              <tr key={p.key}>
                <th scope="row">
                  {p.name}
                  {p.is_protected && <span className="soon">owner only</span>}
                  {p.description && <span className="desc muted">{p.description}</span>}
                </th>
                {roles.map((r) => (
                  <td key={r.code} className={holds(r, p.key) ? "held" : "not-held"}>
                    <span aria-hidden="true">{holds(r, p.key) ? "\u2713" : "\u2013"}</span>
                    <span className="visually-hidden">
                      {r.name} {holds(r, p.key) ? "has" : "does not have"}: {p.name}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

function RoleHelpDrawer({ roles, onClose }: { roles: Role[]; onClose: () => void }) {
  // "Compare" is the wider view, so the drawer widens with it rather than
  // squeezing nine columns into a reading-width panel.
  const [view, setView] = useState<"list" | "compare">("list");
  const [matrix, setMatrix] = useState<RoleMatrix | null>(null);
  const [matrixError, setMatrixError] = useState<string | null>(null);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  // Only when it is asked for: the catalogue is two more queries, and most
  // people open this drawer to read one description and close it again.
  useEffect(() => {
    if (view !== "compare" || matrix) return;
    let live = true;
    listRoleMatrix()
      .then((m) => { if (live) setMatrix(m); })
      .catch((e) => {
        if (live) setMatrixError(e.message ?? String(e));
        void logError("app", e, { step: "load_role_matrix" });
      });
    return () => { live = false; };
  }, [view, matrix]);

  const listed = roles.filter((item) => !item.is_owner);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className={view === "compare" ? "side-drawer wide" : "side-drawer"}
             role="dialog" aria-modal="true"
             aria-labelledby="role-help-title" onClick={(event) => event.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <h2 id="role-help-title">Roles and access</h2>
            <p className="muted">Choose what this person may do, not their job title.</p>
          </div>
          <button type="button" className="drawer-close" aria-label="Close role help"
                  onClick={onClose}>×</button>
        </div>

        <section className="drawer-section">
          <h3>A role is a bundle of permissions</h3>
          <p>
            A video editor and a copywriter are both <strong>Creators</strong>. Creating a post,
            approving it, scheduling it and publishing it are separate authorities, so someone can
            prepare content without being able to put it in front of your audience.
          </p>
        </section>

        <section className="drawer-section">
          <div className="role-help-head">
            <h3>Available roles</h3>
            <div className="segmented" role="tablist" aria-label="How to view the roles">
              <button type="button" role="tab" aria-selected={view === "list"}
                      className={view === "list" ? "on" : undefined}
                      onClick={() => setView("list")}>What each does</button>
              <button type="button" role="tab" aria-selected={view === "compare"}
                      className={view === "compare" ? "on" : undefined}
                      onClick={() => setView("compare")}>Compare</button>
            </div>
          </div>

          {/* The three roles most often confused. Static, matching the
              role_permissions seed; Compare below is the live source. */}
          <div className="table-scroll">
            <table className="plans role-compare">
              <thead>
                <tr><th>Role</th><th>Create</th><th>Upload</th><th>Publish</th></tr>
              </thead>
              <tbody>
                <tr><td>Creator</td><td>✓</td><td>✓</td><td>✗</td></tr>
                <tr><td>Publisher</td><td>✗</td><td>✗</td><td>✓</td></tr>
                <tr><td><strong>Producer</strong></td><td>✓</td><td>✓</td><td>✓</td></tr>
              </tbody>
            </table>
          </div>
          <p className="muted">To post from WhatsApp, a person needs all three: use Producer.</p>

          {view === "list" ? (
            <div className="role-help-list">
              {listed.map((item) => (
                <div className="role-help-item" key={item.code}>
                  <strong>{item.name}</strong>
                  <p className="muted">{item.description}</p>
                </div>
              ))}
            </div>
          ) : matrixError ? (
            <p className="err">{matrixError}</p>
          ) : matrix ? (
            <>
              <p className="muted">
                A tick is a permission the server checks, not a promise about it. Owner is shown
                because it holds everything, including permissions added later; the rest hold
                exactly what is ticked.
              </p>
              <RoleCompareTable roles={roles} matrix={matrix} />
            </>
          ) : (
            <Loading label="Loading the permission list…" />
          )}
        </section>

        <section className="drawer-section relationship-note">
          <h3>Employee or agency?</h3>
          <p>
            That is their <strong>relationship</strong> to your organisation, and it is a separate
            question from their role. An employee and an agency collaborator can both be a Creator,
            Reviewer or Publisher, and the permissions are identical either way.
          </p>
          <p>
            What the relationship changes is what this screen shows you and what it defaults to.
            Outside people are grouped by their company, they cannot be made Admin, and their access
            is given an end date unless you remove it.
          </p>
        </section>

        <section className="drawer-section">
          <h3>Safer setup</h3>
          <ul>
            <li>Give each person their own invitation; do not share an agency login.</li>
            <li>Choose the least powerful role that covers their work.</li>
            <li>Limit outside collaborators to one account when they do not need the workspace.</li>
            <li>Set an end date that matches the engagement, not one you hope to remember.</li>
          </ul>
        </section>
      </aside>
    </div>
  );
}
